import moment from "moment";
import { IDropdownOption } from "@fluentui/react";
import isDev from "src/utils/isDev";
import api from "src/spintr/SpintrApi";
import { ILocalizationDictionary } from "src/marketplace/types";

import "moment/dist/locale/de";
import "moment/dist/locale/fi";
import "moment/dist/locale/nb"; // Norskt bokmål
import "moment/dist/locale/sv";
import "moment/dist/locale/en-gb";
import "moment/dist/locale/da";
import "moment/dist/locale/it";
import "moment/dist/locale/es";
import "moment/dist/locale/fr";

let tags: { [key: string]: string };
let currentLanguage: any;
let hasTags: boolean;

export type LanguageFetchCallback = (success: boolean) => void;

tags = {};
currentLanguage = 0;
hasTags = false;

const unlocalizedStrings = {};
const languageCodes = ["sv", "nb", "en-gb", "de", "fi", "da", "it", "es", "fr"];

if (isDev) {
    // @ts-ignore
    window.getUnlocalizedStrings = function RetrieveUnlocalizedStrings() {
        return Object.keys(unlocalizedStrings);
    }
}
/**
 * Consumes and stores a provided localization tag dictionary.
 *
 * @param items Localization tags to be stored.
 */
export function consumeLocalization(items: { [key: string]: string }): void {
    tags = items;
}

const getInitialMomentLanguage = () => {
    const navLang = navigator.language;
    const defaultLang = languageCodes[2];

    if (!navLang) {
        return defaultLang;
    }

    for (const languageCode of languageCodes) {
        if (navLang.split("-")[0] === languageCode.split("-")[0]) {
            return languageCode;
        }
    }

    return defaultLang;
}

moment.locale(getInitialMomentLanguage());

/**
 * Specifies what language moment should use
 *
 * @param langId Language id
 */
export function setMomentLang(langId: number): void {
    const langCode = languageCodes[langId - 1];
    document.documentElement.lang = langCode;
    moment.locale(langCode);
}

export function getLangCode(langId: number): string {
    const langCode = languageCodes[langId - 1];
    return langCode;
}

/**
 * Fetches and stores the correct localization tags from the server.
 *
 * @param api An API object that will be used to execute the request.
 * @param next Function to be called once loading is completed.
 */
export const fetchLangTags = (() => {
    let isLoading: boolean = false;

    return (next: LanguageFetchCallback): void => {
        if (isLoading) {
            return;
        }

        isLoading = true;

        api.get("/api/v1/language").then(
            (response: any) => {
                consumeLocalization(response.data.tags);
                next(true);
                currentLanguage = response.data.currentLanguage;
                isLoading = false;
            },
            () => {
                next(false);

                isLoading = false;
            }
        );
    };
})();

/**
 * Returns the current localizationtag
 *
 */
export const getLocalizationTag = () => {
    switch (currentLanguage) {
        case "1":
            return "sv";
        case "2":
            return "no";
        case "3":
            return "en";
        case "4":
            return "de";
        case "5":
            return "fi";
        case "6":
            return "da";
        case "7":
            return "it";
        case "8":
            return "es";
        case "9":
            return "fr";
    }
};

/**
 * Localizes a string based on the provided key.
 *
 * @param key A localization key.
 * @returns The localized string if the key could be found, otherwise the key.
 */
const localize = (key: string, skipLog?: boolean) => {
    if (tags && hasTags === false) {
        hasTags = Object.keys(tags).length > 0;
    }

    if (!tags || !hasTags) {
        if (!skipLog && isDev) {
            console.log("Language tags not fetched yet but tried to get tag: " + key);
        }

        return key;
    }

    const tag = tags[key];

    if (!tag) {
        if (!skipLog && isDev) {
            const message = "Language tag not found: " + key;
            console.warn ? console.warn(message) : console.log(message);
            unlocalizedStrings[key] = true;
        }

        return key;
    }

    return tag;
};
export default localize;

const systemTargetGroupTags: string[] = ["Administratorer", "Redaktorer"];

/**
 * Localizes the name of a system generated target group.
 *
 * System generated target groups store their language tag as their name so that the
 * name can follow the reader's language. Every other target group is named by the
 * customer and is returned untouched.
 *
 * @param name A target name.
 * @returns The localized name for a system generated target group, otherwise the name.
 */
export const localizeTargetName = (name: string): string =>
    systemTargetGroupTags.indexOf(name) > -1 ? localize(name) : name;

/**
 * Replaces format placeholders and returns the formatted string.
 *
 * The function looks for format placeholders based around the number provided
 * in the index argument wrapped with curly brackets. E.g.: {0}, {1} and so on.
 *
 * @param input Input string containing format placeholders.
 * @param replacement Data to replace the format placeholder with.
 * @param index Zero based format placeholder.
 * @returns Formatted string
 */
export const replaceFormatIndex = (input: string, replacement: any, index: number): string =>
    input.replace(new RegExp(`\\{${index}\\}`, "g"), replacement);

/**
 * Localizes a string based on the provided key and formats the input data.
 *
 * Example:
 * Given that key "FOO" corresponds to the string "Hello {0}, I'm {1}"
 * Then calling the function with parameters "FOO", "foo", "bar" will return
 * "Hello foo, I'm bar"
 *
 * @param key A localization key
 * @param args Any arguments to be formatted into the localized string.
 * @returns The localized string if the key could be found, otherwise the key.
 */
export const localizeFormat: (key: string, ...args: any) => string = (key: string, ...args: any[]) =>
    args.reduce(replaceFormatIndex, localize(key));

/**
 *
 * @param typeId A typeId
 * @param options Able to change the word to different states
 */

export type TypeNameOptions = {
    case?:      "indefinite" | "definite" | "possessive" | "lemma" | undefined;
    form?:      "singular" | "plural" | undefined;
    alternative?: boolean;
};

export const getTypeName = (typeId: number, options: TypeNameOptions = {}): string => {
    let tag: string;

    options = Object.assign(
        {},
        {
            case: "indefinite" /* indefinite | definite | possessive | lemma */,
            form: "singular" /* singular | plural */,
        },
        options
    );

    tag = "Ubertype" + typeId + "_" + (options.form === "plural" ? 1 : 0) + "_";
    switch (options.case) {
        case "indefinite":
            tag += 0;
            break;
        case "definite":
            tag += 1;
            break;
        case "possessive":
            tag += 2;
            break;
        default:
        case "lemma":
            tag += 4;
            break;
    }

    if (options.alternative) {
        tag += "_alternative";
    }

    return localize(tag);
};

export const getTypeNameCapitalized = (typeId: number, options: TypeNameOptions = {}): string => {
    const typeName = getTypeName(typeId, options);
    return typeName.charAt(0).toUpperCase() + typeName.slice(1);
};

export const getLanguageOptions = (): IDropdownOption[] =>  [
    { key: "", text: localize("EjAngivet") },
    ...[
        { key: "sv", text: localize("Swedish") },
        { key: "nb", text: localize("Norwegian") },
        { key: "en", text: localize("English") },
        { key: "de", text: localize("German") },
        { key: "fi", text: localize("Finnish") },
        { key: "dk", text: localize("Danish") },
        { key: "it", text: localize("Italian") },
        { key: "es", text: localize("Spanish") },
        { key: "fr", text: localize("French") },
    ].sort((a, b) => (a.text > b.text ? 1 : -1)),
];

export const localizeFromDict = (dict: ILocalizationDictionary, tag: string) => {
    const lang = getLocalizationTag();

    if (dict[lang] && dict[lang][tag]) {
        return dict[lang][tag];
    }

    if (dict["sv"] && dict["sv"][tag]) {
        //sv as fallback
        return dict["sv"][tag];
    }

    return tag;
}
